import React, { useState, useEffect, useMemo } from 'react'
import moment from "moment"
import classNames from 'classnames'
import _ from 'lodash'
import {
    List,
    ListItem,
    Typography,
} from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { DayPickerSingleDateController } from 'react-dates'
import "react-dates/initialize"
import isInclusivelyAfterDay from 'react-dates/lib/utils/isInclusivelyAfterDay'
import isSameDay from 'react-dates/lib/utils/isSameDay'
import { HORIZONTAL_ORIENTATION } from 'react-dates/constants'
import { useIntl } from 'src/common/razzle-platform-translate'
import TimePicker from './TimePicker'
import DefaultIndicators from '../components/DefaultIndicators'
import DefaultDisplayDaysResult, { daysInUnity } from '../components/DefaultDisplayDaysResult'
import { DefaultCancelButton, DefaultConfirmButton, DefaultInitializeButton} from '../components/DefaultActionsButtons'
import { ReservationDialog } from '../components/ReservationDialog'
import 'react-dates/lib/css/_datepicker.css'
import './ReservationPicker.css'


const useStyles = makeStyles((theme) => ({
    bodyContainer: {
        display: 'flex',
        paddingLeft: 21,
        paddingRight: 21,
        paddingTop: 0,
        paddingBottom: 12,
        flexDirection: 'column',
        [theme.breakpoints.down('sm')]: {
            paddingRight: 0,
            paddingLeft: 0,
        }
    },
    root: {
        width: '100%',
        backgroundColor: theme.palette.background.paper,
    },
    dataPresentationContainer: {
        display: 'flex',
        justifyContent: 'start',
        padding: '5px 0px 15px 0'
    },
    hoursPresentationTitle: {
        height: 50,
        color: "#fff",
        fontWeight: 'bold',
        paddingLeft: 10,
        background: theme.palette.primary.main
    },
    priceContainer: {
        position: 'absolute',
        maxWidth: 50,
        bottom: -6,
        fontWeight: 500,
        fontSize: 12,
        [theme.breakpoints.down('xs')]: {
            maxWidth: 40,
        },
    }
}))



const defaultProps = {
    // example props for the demo
    autoFocus: false,
    initialDate: null,
    showInput: false,

    // day presentation and interaction related props
    renderCalendarDay: undefined,
    renderDayContents: null,
    isDayBlocked: () => false,
    isOutsideRange: day => !isInclusivelyAfterDay(day, moment()),
    isDayHighlighted: () => false,
    enableOutsideDays: false,

    // calendar presentation and interaction related props
    orientation: HORIZONTAL_ORIENTATION,
    withPortal: false,
    initialVisibleMonth: null,
    numberOfMonths: 2,
    onOutsideClick() { },
    keepOpenOnDateSelect: false,
    renderCalendarInfo: null,
    isRTL: false,

    // navigation related props
    navPrev: null,
    navNext: null,
    renderNavPrevButton: null,
    renderNavNextButton: null,
    onPrevMonthClick() { },
    onNextMonthClick() { },

    // internationalization
    monthFormat: 'MMMM YYYY',
    indicators: DefaultIndicators,
    displayDaysResult: DefaultDisplayDaysResult,
    cancelButton: DefaultCancelButton,
    confirmButton: DefaultConfirmButton,
    initializeButton: DefaultInitializeButton,
}


export const START_DATE = 'startDate'
export const END_DATE = 'endDate'

const VIEW = {
    DAYS: 'days',
    HOURS: 'hours'
}


function ReservationPicker(props) {

    const {
        availabilities: originalAvailabilities,
        inProgress,
        onConfirm,
        periodicities,
        indicators: Indicators,
        displayDaysResult: DisplayDaysResult,
        cancelButton: CancelButton,
        confirmButton: ConfirmButton,
        reservationButton: ReservationButton,
        initializeButton: InitializeButton
    } = props

    const theme = useTheme()
    const isSm = useMediaQuery(theme.breakpoints.down('sm'))
    const { formatMessage } = useIntl()
    const classes = useStyles()
    const [error, setError] = useState({})
    const [focused, setFocused] = useState(true)
    const [date, setDate] = useState(null)
    const [numberOfUnity, setNumberOfUnity] = useState(null)
    const [selectedAvailability, setSelectedAvailability] = useState(null)
    const [availablitiesTimesOfSelectedDay, setAvailablitiesTimesOfSelectedDay] = useState([])
    const [view, setView] = useState(null)
    const [extraData, setExtraData] = useState({})


    const unityLables = {
        hour: formatMessage({ id: 'Heures', defaultMessage: 'Heures' }),
        day: formatMessage({ id: 'Journées', defaultMessage: 'Journées' }),
        week: formatMessage({ id: 'Semaines', defaultMessage: 'Semaines' }),
        month: formatMessage({ id: 'Mois', defaultMessage: 'Mois' }),
        year: formatMessage({ id: 'Années', defaultMessage: 'Années' })
    }

    const availabilities = useMemo(() => {
        return _.reduce(originalAvailabilities, (previous, current) => {
            const e = _.find(previous, prev =>
                prev.isAvailable === true
                && current.isAvailable === true
                && prev.unit === current.unit
                && prev.unitNumbers === current.unitNumbers
                && prev.periodicityId === current.periodicityId
                && prev.endDate.isSame(current.startDate))

            if (e) {
                return _.map(previous, (prev) => prev.id === e.id ? ({
                    ...prev,
                    startDate: e.startDate,
                    endDate: current.endDate,
                    subAvailabilities:  [...prev.subAvailabilities ? prev.subAvailabilities : [prev], current]
                }) : prev)
            }
            return [...previous, current]
        }, [])

    }, [originalAvailabilities])

    const calculateEndDate = (numberOfUnity) => {
        if (date && view === VIEW.DAYS) {
            if (selectedAvailability.unit === 'month') {
                return date.clone().set('month', date.get('month') + numberOfUnity * selectedAvailability.unitNumbers )
            } else if (selectedAvailability.unit === 'year') {
                return date.clone().set('year', date.get('year') + numberOfUnity * selectedAvailability.unitNumbers )
            } else {
                return date.clone().add(numberOfUnity * selectedAvailability.unitNumbers * daysInUnity[selectedAvailability.unit], 'days')
            }
        }
        return null
    }
    const endDate = useMemo(() => {
        return calculateEndDate(numberOfUnity)
    },[date, selectedAvailability, view, numberOfUnity])

    useEffect(() => {
        // validate the unity number
        if (date && view === VIEW.DAYS) {
            let deltaTime
            let error = null
            const errorMsg = `${formatMessage({ id: "La valeur minimale que doit selectionner c'est", defaultMessage: "La valeur minimale que doit selectionner c'est" })} ${selectedAvailability.unitNumbers * selectedAvailability.minPeriodicity} ${unityLables[selectedAvailability.unit]}`
            if (selectedAvailability.unit === 'month') {
                deltaTime = endDate.diff(date.clone(), 'months')
                if (deltaTime < selectedAvailability.unitNumbers * selectedAvailability.minPeriodicity) {
                    error = errorMsg
                }
            } else if (selectedAvailability.unit === 'year') {
                deltaTime = endDate.diff(date.clone(), 'years')
                if (deltaTime < selectedAvailability.unitNumbers * selectedAvailability.minPeriodicity) {
                    error = errorMsg
                }
            } else {
                deltaTime = endDate.diff(date.clone(), 'days')
                if (deltaTime < selectedAvailability.unitNumbers * selectedAvailability.minPeriodicity * daysInUnity[selectedAvailability.unit]) {
                    error = errorMsg
                }
            }

            setError((currentError) => ({
                ...currentError,
                numberOfUnity: error,
            }))
        }
    }, [numberOfUnity])

    useEffect(() => {
        // validate the end date
        if (date && view === VIEW.DAYS) {
            let deltaTime
            if (selectedAvailability.unit === 'month') {
                deltaTime = endDate.diff(selectedAvailability.endDate.clone(), 'months')
            } else if (selectedAvailability.unit === 'year') {
                deltaTime = endDate.diff(selectedAvailability.endDate.clone(), 'years')
            } else {
                deltaTime = endDate.diff(selectedAvailability.endDate.clone(), 'days')
            }

            setError((error) => ({
                ...error,
                endDate: deltaTime > 0 ? `${formatMessage({ id: 'Il fault séléctionner', defaultMessage: 'Il fault séléctionner' })} ${selectedAvailability.unitNumbers} ${unityLables[selectedAvailability.unit]} ${ formatMessage({ id: 'ou plus', defaultMessage: 'ou plus' })}` : null,
            }))
        }
    }, [date])

    const handleChange = (currentDate) => {
        const selectedDate = moment(currentDate.clone().set('h', 0).format("YYYY-MM-DD HH:mm"), "YYYY-MM-DD HH:mm")
        const availablitiesTimes = _.filter(availabilities, availability => availability.unit === 'hour' && isSameDay(availability.startDate, selectedDate))

        const daysDifference = date ? selectedDate.diff(date, 'days') : 0
        if (daysDifference > 0) {
            const newEndDate = calculateEndDate(daysDifference + 1)
            const isOutsideRange = newEndDate.diff(selectedAvailability.endDate, 'days') > 0
            if (!isOutsideRange) {
                setNumberOfUnity(daysDifference + 1)
                return
            }
        }

        setDate(selectedDate)

        if (!_.isEmpty(availablitiesTimes)) {
            setAvailablitiesTimesOfSelectedDay(availablitiesTimes)
            setSelectedAvailability(null)
            setView(VIEW.HOURS)
            setNumberOfUnity(null)
        } else {

            const availability = _.find(availabilities, availability => currentDate.isBetween(availability.startDate, availability.endDate))

            setView(VIEW.DAYS)
            setSelectedAvailability(availability)
            setNumberOfUnity(availability.minPeriodicity)
        }
        setExtraData({})
    }

    const initialize = (date) => {
        setDate(null)
        setSelectedAvailability(null)
        setNumberOfUnity(null)
    }

    const dayPickerProps = _.omit(props, [
        'autoFocus',
        'initialDate',
        'showInput',
        'onCancel',
        'onConfirm',
        'availabilities',
        't',
        'tReady',
        'i18n',
        'inProgress',
        'indicators',
        'displayDaysResult',
        'cancelButton',
        'confirmButton',
        'initializeButton',
        'periodicities'
    ]);

    const handleConfirm = () => {

        const availabilitiesToReserve = []

        if (selectedAvailability.subAvailabilities) {
            const _startDate = date.clone().add(1, 'm')
            const _endDate = endDate.clone().subtract(1, 'm')

            for (const subAvailability of selectedAvailability.subAvailabilities) {

                const isStartDateBetweenSubAvailabilityDays = _startDate.isBetween(subAvailability.startDate, subAvailability.endDate)
                const isEndDateBetweenSubAvailabilityDays = _endDate.isBetween(subAvailability.startDate, subAvailability.endDate)


                if (isStartDateBetweenSubAvailabilityDays && isEndDateBetweenSubAvailabilityDays) {
                    availabilitiesToReserve.push({
                        startDate: date.format('DD-MM-YYYY HH:mm'),
                        numPeriodicity: numberOfUnity,
                        periodicity: subAvailability.periodicityId,
                        ...extraData

                    })
                } else if (isStartDateBetweenSubAvailabilityDays) {
                    availabilitiesToReserve.push({
                        startDate: date.format('DD-MM-YYYY HH:mm'),
                        numPeriodicity: subAvailability.endDate.diff(date, 'days') / subAvailability.unitNumbers,
                        periodicity: subAvailability.periodicityId,
                        ...extraData
                    })
                } else if (isEndDateBetweenSubAvailabilityDays) {


                    availabilitiesToReserve.push({
                        startDate: subAvailability.startDate.format('DD-MM-YYYY HH:mm'),
                        numPeriodicity: endDate.diff(subAvailability.startDate, 'days') / subAvailability.unitNumbers,
                        periodicity: subAvailability.periodicityId,
                        ...extraData
                    })
                } else if (subAvailability.startDate.isBetween(date, endDate) && subAvailability.endDate.isBetween(date, endDate)) {
                    availabilitiesToReserve.push({
                        startDate: subAvailability.startDate.format('DD-MM-YYYY HH:mm'),
                        numPeriodicity: subAvailability.endDate.diff(subAvailability.startDate, 'days') / subAvailability.unitNumbers,
                        periodicity: subAvailability.periodicityId,
                        ...extraData
                    })
                }
            }
        } else {
            availabilitiesToReserve.push({
                startDate: date.format('DD-MM-YYYY HH:mm'),
                numPeriodicity: numberOfUnity,
                periodicity: selectedAvailability.periodicityId,
                ...extraData
            })
        }
        onConfirm(availabilitiesToReserve)
    }

    return (
        <ReservationDialog reservationButton={ReservationButton} >
            { ({ onClose, ContentContainer, ActionsButtonsContainer }) => (
                <>
                    <ContentContainer>
                        <DayPickerSingleDateController
                            {...dayPickerProps}
                            onDateChange={handleChange}
                            onFocusChange={() => setFocused(true)}
                            focused={focused}
                            showKeyboardShortcuts={false}
                            hideKeyboardShortcutsPanel={true}
                            numberOfMonths={isSm ? 1 : 2}
                            noBorder
                            isOutsideRange={day => {
                                const currentDay = day.clone().set('h', 0).set('m', 1)

                                const isReserved = _.some(_.filter(availabilities, {isAvailable: false}), availability => {
                                    if (availability.unit === 'hour') return isSameDay(availability.startDate, currentDay)
                                    return currentDay.isBetween(availability.startDate, availability.endDate)
                                })

                                const isAvailable = _.some(_.filter(availabilities, {isAvailable: true}), availability => {
                                    if (availability.unit === 'hour') return isSameDay(availability.startDate, currentDay)
                                    return currentDay.isBetween(availability.startDate, availability.endDate)
                                })

                                return !isInclusivelyAfterDay(currentDay, moment()) || isReserved || !isAvailable
                            }}
                            date={date}
                            isDayHighlighted={currentDate => !!date && view === VIEW.DAYS && currentDate.isBetween(date, endDate)}
                            renderDayContents={day => {
                                const currentDay = day.clone().set('h', 0).set('m', 1)

                                const isReserved = _.some(_.filter(availabilities, {isAvailable: false}), availability => {
                                    if (availability.unit === 'hour') return false
                                    return currentDay.isBetween(availability.startDate, availability.endDate)
                                })

                                let periodicityId = ''
                                let price = ''
                                const isAvailable = _.some(_.filter(availabilities, {isAvailable: true}), availability => {
                                    if (availability.unit === 'hour') return false
                                    const isBetweenDays = currentDay.isBetween(availability.startDate, availability.endDate)
                                    if (isBetweenDays) {
                                        periodicityId = availability.periodicityId
                                        if (!isReserved) {
                                            // Display the price only if not reserve yet.
                                            if (availability.subAvailabilities) {
                                                for (const subAvailability of availability.subAvailabilities) {
                                                    const isBetweenSubAvailabilityDays = currentDay.isBetween(subAvailability.startDate, subAvailability.endDate)
                                                    if (isBetweenSubAvailabilityDays) {
                                                        price = subAvailability.price                                                        
                                                    }
                                                }
                                            } else {
                                                price = availability.price
                                            }
                                        }
                                    }
                                    return isBetweenDays
                                })

                                let className
                                if (isInclusivelyAfterDay(currentDay, moment()) && !isReserved && isAvailable) {
                                    className = `periodicity-${periodicityId}`
                                }
                                return (
                                    <div className={className} style={{ minHeight: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                                        <span style={{ position: 'absolute', top: -6}}>{day.format('D')}</span>
                                        { price &&
                                            <span className={classNames(classes.priceContainer, 'truncate')}>{price} { formatMessage({ id: '€', defaultMessage: '€' }) }</span>
                                        }
                                    </div>
                                )
                            }}
                            daySize={isSm ? 45 : 55}
                        />
                        <Indicators periodicities={periodicities} isMobile={isSm} />

                        { date && view === VIEW.HOURS && !_.isEmpty(availablitiesTimesOfSelectedDay) &&
                            <div className={classes.bodyContainer}>
                                <div>
                                    <div className={classes.dataPresentationContainer} >
                                        <List className={classes.root}>

                                            {!selectedAvailability &&
                                                <ListItem role={undefined} dense className={classes.hoursPresentationTitle}>
                                                    <Typography>{ formatMessage({ id: 'Les Horaires disponible', defaultMessage: 'Les Horaires disponible' }) }</Typography>
                                                </ListItem>
                                            }
                                            {availablitiesTimesOfSelectedDay.map((time, index) => (
                                                    <>
                                                        {   !selectedAvailability ?
                                                            <TimePicker
                                                                time={time}
                                                                index={index}
                                                                error={error.time}
                                                                onError={error => setError((currentError) => ({...currentError, time: error}))}
                                                                onSelectedItem={item => {
                                                                    if (!item) setNumberOfUnity(null)
                                                                    setSelectedAvailability(item)

                                                                }}
                                                                onChange={({startDate, numberOfUnity}) => {
                                                                    setDate(startDate)
                                                                    setNumberOfUnity(numberOfUnity)
                                                                }}
                                                                isSelected={false}
                                                            />
                                                            : time.id === selectedAvailability.id &&
                                                            <TimePicker
                                                                time={time}
                                                                index={index}
                                                                error={error.time}
                                                                onError={error => setError((currentError) => ({...currentError, time: error}))}
                                                                onSelectedItem={item => {
                                                                    if (!item) setNumberOfUnity(null)
                                                                    setSelectedAvailability(item)

                                                                }}
                                                                onChange={({startDate, numberOfUnity}) => {
                                                                    setDate(startDate)
                                                                    setNumberOfUnity(numberOfUnity)
                                                                }}
                                                                isSelected={true}
                                                            />
                                                        }
                                                    </>
                                                ))
                                            }
                                        </List>
                                    </div>
                                </div>
                            </div>
                        }
                        { date && view === VIEW.DAYS &&
                            <div className={classes.bodyContainer}>
                                <DisplayDaysResult
                                    startDate={date}
                                    endDate={endDate}
                                    error={error}
                                    selectedAvailability={selectedAvailability}
                                    unityLables={unityLables}
                                    availabilities={availabilities}
                                    numberOfUnity={numberOfUnity}
                                    onNumberOfUnityChange={(value) => setNumberOfUnity(value)}
                                    extraData={extraData}
                                    onExtraDataChange={(value) => setExtraData(value)}
                                    periodicities={periodicities}
                                />
                            </div>
                        }
                        </ContentContainer>

                        <ActionsButtonsContainer>
                            <>
                                <div className="flex items-center">
                                    <CancelButton onCancel={onClose} disabled={inProgress} />
                                </div>
                                <div className="flex items-center">
                                    <InitializeButton onInitialize={initialize} disabled={inProgress} />
                                </div>
                                <div className="flex items-center justify-end">
                                    <ConfirmButton
                                        onConfirm={handleConfirm}
                                        disabled={!(!inProgress && date && numberOfUnity && _.isEmpty(_.omitBy(error, _.isEmpty)))}
                                        inProgress={inProgress}
                                        extraData={extraData}
                                        unityLables={unityLables}
                                        availabilities={availabilities}
                                        numberOfUnity={numberOfUnity}
                                        startDate={date}
                                        endDate={endDate}
                                        error={error}
                                        selectedAvailability={selectedAvailability}
                                    />
                                </div>
                            </>
                        </ActionsButtonsContainer>
                        </>
                        )}

                        </ReservationDialog>
    )

}

ReservationPicker.defaultProps = defaultProps

export default ReservationPicker
